(() => {
  const SECTION_NAME = 'free-gift-eligibility';
  const MAX_PASSES = 5;

  let syncing = false;
  let pendingSync = false;
  let pendingClearRemoval = false;
  let giftRemoved = false;
  let prevGiftQty = null;
  let storageKey = null;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  async function fetchCart() {
    const res = await fetch(`${root()}cart.js`, { cache: 'no-store' });
    return res.json();
  }

  async function fetchGiftState() {
    const res = await fetch(`${root()}?sections=${SECTION_NAME}`, { cache: 'no-store' });
    if (!res.ok) return null;

    const data = await res.json();
    const html = data[SECTION_NAME];
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.getElementById('free-gift-state');
    if (!el) return null;

    return {
      enabled: el.dataset.enabled === 'true',
      giftVariantId: Number(el.dataset.giftVariantId) || 0,
      desiredQty: Number(el.dataset.desiredQty) || 0
    };
  }

  function setRemovalFlag(variantId, removed) {
    storageKey = `giftRemoved_${variantId}`;
    giftRemoved = removed;
    if (removed) {
      localStorage.setItem(storageKey, 'true');
    } else {
      localStorage.removeItem(storageKey);
    }
  }

  function loadRemovalFlag(variantId) {
    storageKey = `giftRemoved_${variantId}`;
    giftRemoved = localStorage.getItem(storageKey) === 'true';
  }

  // Section IDs the theme needs re-rendered alongside a cart mutation
  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return sections;
  }

  function publishCart(cart, sectionIds) {
    const sections = cart && cart.sections;
    const complete = sections && sectionIds.every((id) => typeof sections[id] === 'string');

    if (complete) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'free-gift:sync', cart }
      }));
      return;
    }

    // Without rendered section HTML the theme's cart:change handler throws,
    // so fall back to the event that re-fetches sections itself.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function addGift(variantId, quantity, sectionIds) {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity, sections: sectionIds.join(',') })
    });
    if (!res.ok) return null;

    const added = await res.json();
    const cart = await fetchCart();
    cart.sections = added.sections;
    return cart;
  }

  async function updateLines(updates, sectionIds) {
    const res = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates, sections: sectionIds.join(',') })
    });
    if (!res.ok) return null;

    return res.json();
  }

  // A discount can split the gift across several lines, so the total quantity is
  // what gets enforced; the split itself is left alone.
  async function setGiftQty(variantId, targetQty, sectionIds) {
    const cart = await fetchCart();
    const giftLines = cart.items.filter((item) => item.variant_id === variantId);
    const currentQty = giftLines.reduce((total, item) => total + item.quantity, 0);

    if (currentQty === targetQty) return null;

    if (currentQty < targetQty) {
      return addGift(variantId, targetQty - currentQty, sectionIds);
    }

    // Trim from the last lines first, keeping every key in a single request so
    // that re-splitting can't invalidate a key mid-update.
    let remaining = targetQty;
    const updates = {};

    giftLines.forEach((line) => {
      const keep = Math.min(line.quantity, remaining);
      updates[line.key] = keep;
      remaining -= keep;
    });

    return updateLines(updates, sectionIds);
  }

  async function syncGift({ clearManualRemoval = false } = {}) {
    if (clearManualRemoval) pendingClearRemoval = true;

    if (syncing) {
      pendingSync = true;
      return;
    }

    syncing = true;
    let passes = 0;

    try {
      do {
        pendingSync = false;

        if (++passes > MAX_PASSES) break;

        const state = await fetchGiftState();
        if (!state || !state.enabled || !state.giftVariantId) {
          pendingClearRemoval = false;
          break;
        }

        const { giftVariantId, desiredQty } = state;

        loadRemovalFlag(giftVariantId);

        if (pendingClearRemoval) {
          setRemovalFlag(giftVariantId, false);
          pendingClearRemoval = false;
        }

        const cart = await fetchCart();
        const giftQty = cart.items
          .filter((item) => item.variant_id === giftVariantId)
          .reduce((total, item) => total + item.quantity, 0);

        // Customer deleted the gift while still eligible
        if (prevGiftQty > 0 && giftQty === 0 && desiredQty > 0) {
          setRemovalFlag(giftVariantId, true);
        }
        prevGiftQty = giftQty;

        // A dismissed gift is never re-added, but it is still capped at what was earned
        let targetQty = desiredQty;
        if (giftRemoved) targetQty = Math.min(giftQty, desiredQty);

        if (targetQty === 0 && giftQty > 0) {
          setRemovalFlag(giftVariantId, false);
        }

        if (giftQty === targetQty) continue;

        const sectionIds = bundledSections();
        const updatedCart = await setGiftQty(giftVariantId, targetQty, sectionIds);
        if (!updatedCart) continue;

        prevGiftQty = targetQty;
        publishCart(updatedCart, sectionIds);
      } while (pendingSync);
    } catch (err) {
      console.error('[free-gift]', err);
    } finally {
      syncing = false;
    }
  }

  document.addEventListener('variant:add', () => {
    syncGift({ clearManualRemoval: true });
  });

  document.addEventListener('cart:change', (evt) => {
    if (evt.detail && evt.detail.baseEvent === 'free-gift:sync') return;
    syncGift();
  });

  window.addEventListener('pageshow', (evt) => {
    if (evt.persisted) {
      syncGift({ clearManualRemoval: true });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncGift());
  } else {
    syncGift();
  }
})();
